import { http } from '@/core/helpers/http.helper';
import { ApiResource } from '@/core/models/api-resource.interface';
import axios, { CancelToken } from 'axios';
import {
   ResumeBatchFileStatus,
   ResumeDuplicatePayload,
   ResumeFileReviewData,
   ResumeParsedCandidateData,
   ResumeSimilarCandidatesResponse,
   ResumeUploadBatch,
   ResumeUploadBatchFile,
   ResumeUploadManifestFile,
} from './resume-upload.interface';

const BASE_PATH = '/candidates/resume/batches';

export class ResumeUploadService {
   async getFileStatuses(cancelToken?: CancelToken) {
      const response = await http.get<ApiResource<Array<ResumeBatchFileStatus>>>('/candidates/resume/batch-file-statuses', {
         cancelToken,
      });
      return response.data;
   }

   async createBatch(files: Array<ResumeUploadManifestFile>, cancelToken?: CancelToken) {
      const response = await http.post<ApiResource<ResumeUploadBatch>>(BASE_PATH, { files }, { cancelToken });
      return response.data;
   }

   async getBatches(params: { page?: number; perPage?: number } = {}, cancelToken?: CancelToken) {
      const response = await http.get<ApiResource<Array<ResumeUploadBatch>>>(BASE_PATH, {
         cancelToken,
         params: {
            include: 'status,call_list',
            append: 'counts',
            'filter[active]': 1,
            page: params.page,
            per_page: params.perPage,
         },
      });
      return response.data;
   }

   async getBatch(batchId: number, cancelToken?: CancelToken) {
      const response = await http.get<ApiResource<ResumeUploadBatch>>(`${BASE_PATH}/${batchId}`, {
         cancelToken,
         params: {
            include: 'files,files.status,files.city,files.candidate',
            append: 'counts,resume_url',
         },
      });
      return response.data;
   }

   async getFile(
      batchId: number,
      fileId: number,
      options: { appendReviewUrl?: boolean; appendResumeUrl?: boolean } = {},
      cancelToken?: CancelToken,
   ) {
      const appends = [...(options.appendReviewUrl ? ['review_url'] : []), ...(options.appendResumeUrl ? ['resume_url'] : [])];
      const response = await http.get<ApiResource<ResumeUploadBatchFile>>(`${BASE_PATH}/${batchId}/files/${fileId}`, {
         cancelToken,
         params: {
            include: 'status,city.country,candidate',
            ...(appends.length ? { append: appends.join(',') } : {}),
         },
      });
      return response.data;
   }

   async getParsedResume(identifier: string, cancelToken?: CancelToken) {
      const response = await http.get<ResumeParsedCandidateData>(`/candidates/resume/${identifier}`, { cancelToken });
      return response.data;
   }

   async updateBatch(
      batchId: number,
      metadata: { external_source_id?: number | null; call_list_id?: number | null },
      cancelToken?: CancelToken,
   ) {
      const response = await http.patch<ApiResource<ResumeUploadBatch>>(`${BASE_PATH}/${batchId}`, metadata, { cancelToken });
      return response.data;
   }

   async addFilesToBatch(batchId: number, files: Array<ResumeUploadManifestFile>, cancelToken?: CancelToken) {
      const response = await http.post<ApiResource<ResumeUploadBatch>>(
         `${BASE_PATH}/${batchId}/files`,
         { files },
         {
            cancelToken,
            params: { include: 'files,files.status,files.city,files.candidate', append: 'counts' },
         },
      );
      return response.data;
   }

   async cancelBatch(batchId: number, cancelToken?: CancelToken) {
      const response = await http.post<ApiResource<ResumeUploadBatch>>(
         `${BASE_PATH}/${batchId}/cancel`,
         {},
         { cancelToken, params: { include: 'status,call_list', append: 'counts' } },
      );
      return response.data;
   }

   async confirmBatch(batchId: number, cancelToken?: CancelToken) {
      const response = await http.post<ApiResource<ResumeUploadBatch>>(
         `${BASE_PATH}/${batchId}/confirm`,
         {},
         { cancelToken, params: { include: 'files,files.status,files.city,files.candidate', append: 'counts' } },
      );
      return response.data;
   }

   async deleteFile(batchId: number, fileId: number, cancelToken?: CancelToken) {
      await http.delete(`${BASE_PATH}/${batchId}/files/${fileId}`, { cancelToken });
   }

   async deleteFiles(batchId: number, fileIds: Array<number>, cancelToken?: CancelToken) {
      const response = await http.delete<ApiResource<ResumeUploadBatch>>(`${BASE_PATH}/${batchId}/files`, {
         cancelToken,
         data: { file_ids: fileIds },
      });
      return response.data;
   }

   async updateFileReviewData(batchId: number, fileId: number, data: Partial<ResumeFileReviewData>, cancelToken?: CancelToken) {
      const response = await http.patch<ApiResource<ResumeUploadBatchFile>>(`${BASE_PATH}/${batchId}/files/${fileId}`, data, {
         cancelToken,
         params: { include: 'status,city,candidate' },
      });
      return response.data;
   }

   async reviewFiles(batchId: number, fileIds: Array<number>, cancelToken?: CancelToken) {
      const response = await http.post<ApiResource<ResumeUploadBatch>>(
         `${BASE_PATH}/${batchId}/files/review`,
         { file_ids: fileIds },
         { cancelToken, params: { include: 'files,files.status,files.city,files.candidate', append: 'counts' } },
      );
      return response.data;
   }

   async readyForReviewFiles(batchId: number, fileIds: Array<number>, cancelToken?: CancelToken) {
      const response = await http.post<ApiResource<ResumeUploadBatch>>(
         `${BASE_PATH}/${batchId}/files/ready-for-review`,
         { file_ids: fileIds },
         { cancelToken, params: { include: 'files,files.status,files.city,files.candidate', append: 'counts' } },
      );
      return response.data;
   }

   async validateDuplicate(batchId: number, fileId: number, payload: ResumeDuplicatePayload, cancelToken?: CancelToken) {
      const response = await http.post<ApiResource<ResumeUploadBatchFile>>(
         `${BASE_PATH}/${batchId}/files/${fileId}/validate-duplicate`,
         payload,
         { cancelToken, params: { include: 'status,city.country,candidate' } },
      );
      return response.data;
   }

   async getSimilarCandidates(
      batchId: number,
      fileId: number,
      params: { page?: number; per_page?: number } = {},
      cancelToken?: CancelToken,
   ) {
      const response = await http.get<ResumeSimilarCandidatesResponse>(`${BASE_PATH}/${batchId}/files/${fileId}/similar-candidates`, {
         cancelToken,
         params,
      });
      return response.data;
   }

   async uploadToS3(
      presignedUrl: string,
      file: File,
      options: {
         headers?: Record<string, string | Array<string>>;
         cancelToken?: CancelToken;
         onProgress?: (loaded: number, total: number) => void;
      } = {},
   ) {
      const headers: Record<string, string> = {};
      Object.entries(options.headers ?? {}).forEach(([key, value]) => {
         const normalized = key.toLowerCase();
         if (normalized === 'host' || normalized === 'content-length') return;
         headers[normalized] = Array.isArray(value) ? value.join(',') : value;
      });
      if (!headers['content-type']) headers['content-type'] = file.type;

      await axios.put(presignedUrl, file, {
         headers,
         cancelToken: options.cancelToken,
         maxBodyLength: Infinity,
         maxContentLength: Infinity,
         timeout: 0,
         onUploadProgress: (event) => {
            const total = event.total ?? file.size;
            options.onProgress?.(event.loaded, total);
         },
      });
   }
}
